import React, { useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, ChevronDown, ChevronUp, AlertCircle, Zap } from "lucide-react";
import { Card, CardContent, Badge, Collapsible, CollapsibleTrigger } from "../GraderUI";
import { formatCategoryLabel } from "../categoryLabels";

const PROVIDER_LABELS = {
	openai: "OpenAI",
	gemini: "Google Gemini",
	claude: "Anthropic Claude",
};

const PREVIEW_CHAR_LIMIT = 320;

const dedupByTag = (items) => {
	const seen = new Set();
	return items
		.filter((rec) => {
			if (seen.has(rec.category)) return false;
			seen.add(rec.category);
			return true;
		})
		.slice(0, 3);
};

function splitSummarySentences(text) {
	return text
		.trim()
		.split(/(?<=[.!?])\s+/)
		.filter((s) => s.trim());
}

function getSummaryPreview(fullText) {
	const trimmed = fullText.trim();
	if (!trimmed) return { previewText: "", needsExpand: false };

	const sentences = splitSummarySentences(trimmed);

	if (sentences.length > 2) {
		return {
			previewText: sentences.slice(0, 2).join(" "),
			needsExpand: true,
		};
	}

	if (trimmed.length > PREVIEW_CHAR_LIMIT) {
		return {
			previewText: `${trimmed.slice(0, PREVIEW_CHAR_LIMIT)}…`,
			needsExpand: true,
		};
	}

	return { previewText: trimmed, needsExpand: false };
}

function SummaryListItem({ item, variant }) {
	if (!item) return null;

	const bulletClass =
		variant === "critical"
			? "executive-summary__bullet executive-summary__bullet--critical"
			: "executive-summary__bullet executive-summary__bullet--win";

	return (
		<div className="executive-summary__list-item">
			<div className={bulletClass} />
			<div className="executive-summary__list-content">
				<p className="executive-summary__list-title">{item.title}</p>
				<Badge variant="outline" className="executive-summary__tag">
					{formatCategoryLabel(item.category)}
				</Badge>
			</div>
		</div>
	);
}

export default function ExecutiveSummary({
	overallSummary,
	prioritizedRecommendations = [],
	providerMetrics = [],
	citationRate,
	section,
	topProviderScore,
	citationData,
	mentionRatePct,
}) {
	const [isExpanded, setIsExpanded] = useState(false);

	const criticalIssuesRaw = prioritizedRecommendations
		.filter(
			(rec) =>
				(rec.type === "issue" && rec.impact === "high") ||
				(rec.quadrant === "major_projects" && rec.impact === "high")
		)
		.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
	const criticalIssues = dedupByTag(criticalIssuesRaw);

	const quickWinsRaw = prioritizedRecommendations
		.filter((rec) => rec.quadrant === "quick_wins")
		.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
	let quickWins = dedupByTag(quickWinsRaw);

	if (citationData?.per_provider) {
		const providerNames = {
			openai: "ChatGPT",
			gemini: "Google Gemini",
			claude: "Anthropic Claude",
		};

		for (const [providerKey, providerData] of Object.entries(citationData.per_provider)) {
			if (providerData && providerData.citation_rate < 0.2) {
				const providerName = providerNames[providerKey] || providerKey;
				const citedCount = providerData.cited_count || 0;
				const totalQueries = providerData.queries_tested;
				const citationPercent = Math.round(providerData.citation_rate * 100);

				quickWins = [
					{
						id: -1,
						title: `Low ${providerName} discoverability - Cited in only ${citedCount}/${totalQueries} queries (${citationPercent}%)`,
						category: "ai_discoverability",
						impact: "high",
						effort: "medium",
						priority_score: 25,
						quadrant: "quick_wins",
						type: "recommendation",
					},
					...quickWins,
				];
				break;
			}
		}
	}

	const bestProvider = providerMetrics.reduce(
		(best, current) => {
			const score = current.overall_score ?? current.aeo_score ?? 0;
			return score > best.score ? { name: current.provider, score } : best;
		},
		{ name: "", score: 0 }
	);

	const summaryText = overallSummary || "";
	const { previewText, needsExpand } = getSummaryPreview(summaryText);

	const displayCitationRate =
		citationRate ?? (mentionRatePct != null ? mentionRatePct : undefined);

	const showTopScore =
		section === "aeo" &&
		topProviderScore !== undefined &&
		!(
			bestProvider.name &&
			Math.round(topProviderScore) === Math.round(bestProvider.score)
		);

	const bothListsEmpty = criticalIssues.length === 0 && quickWins.length === 0;
	const rowCount = bothListsEmpty
		? 1
		: Math.max(criticalIssues.length, quickWins.length);

	return (
		<motion.div
			className="executive-summary"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<Card className="executive-summary__card">
				<CardContent className="executive-summary__card-inner">
					<div className="executive-summary__title-row">
						<BarChart3 className="executive-summary__title-icon" aria-hidden />
						<h2 className="executive-summary__title">Executive Summary</h2>
					</div>

					<div className="executive-summary__intro">
						{summaryText ? (
							<Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
								<div className="executive-summary__text">
									{isExpanded ? summaryText : previewText}
								</div>
								{needsExpand && (
									<CollapsibleTrigger className="executive-summary__toggle">
										{isExpanded ? (
											<>
												Show less <ChevronUp className="executive-summary__toggle-icon" />
											</>
										) : (
											<>
												Read more <ChevronDown className="executive-summary__toggle-icon" />
											</>
										)}
									</CollapsibleTrigger>
								)}
							</Collapsible>
						) : (
							<p className="executive-summary__empty">Summary not available</p>
						)}
					</div>

					<div className="executive-summary__issues-block">
						<div className="executive-summary__column-headers">
							<h3 className="executive-summary__column-title">
								<AlertCircle className="executive-summary__column-icon executive-summary__column-icon--critical" />
								Critical issues
							</h3>
							<h3 className="executive-summary__column-title">
								<Zap className="executive-summary__column-icon executive-summary__column-icon--win" />
								Quick wins
							</h3>
						</div>

						{Array.from({ length: rowCount }, (_, index) => (
							<div key={index} className="executive-summary__pair-row">
								<div className="executive-summary__pair-cell">
									{bothListsEmpty && index === 0 ? (
										<p className="executive-summary__pending">Analysis pending</p>
									) : criticalIssues[index] ? (
										<SummaryListItem
											item={criticalIssues[index]}
											variant="critical"
										/>
									) : criticalIssues.length === 0 && index === 0 ? (
										<p className="executive-summary__pending">Analysis pending</p>
									) : null}
								</div>
								<div className="executive-summary__pair-cell">
									{bothListsEmpty && index === 0 ? (
										<p className="executive-summary__pending">Analysis pending</p>
									) : quickWins[index] ? (
										<SummaryListItem item={quickWins[index]} variant="win" />
									) : quickWins.length === 0 && index === 0 ? (
										<p className="executive-summary__pending">Analysis pending</p>
									) : null}
								</div>
							</div>
						))}
					</div>

					<div className="executive-summary__footer">
						<div className="executive-summary__footer-stats">
							{(!section || section === "aeo") && bestProvider.name && (
								<div className="executive-summary__stat">
									<span className="executive-summary__stat-label">Best provider: </span>
									<span className="executive-summary__stat-value">
										{PROVIDER_LABELS[bestProvider.name] ?? bestProvider.name}{" "}
										{Math.round(bestProvider.score)}
									</span>
								</div>
							)}
							{showTopScore && (
								<div className="executive-summary__stat">
									<span className="executive-summary__stat-label">Top score: </span>
									<span className="executive-summary__stat-value">
										{Math.round(topProviderScore)}
									</span>
								</div>
							)}
							{(!section || section === "geo") &&
								displayCitationRate !== undefined &&
								displayCitationRate !== null && (
									<div className="executive-summary__stat">
										<span className="executive-summary__stat-label">Citation rate: </span>
										<span
											className={`executive-summary__stat-value${
												displayCitationRate === 0
													? " executive-summary__stat-value--warning"
													: ""
											}`}
										>
											{Math.round(displayCitationRate)}%
										</span>
									</div>
								)}
						</div>
						<a
							href={section ? `#${section}-action-matrix` : "#action-matrix"}
							className="executive-summary__jump-link"
						>
							Jump to action plan ↓
						</a>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
